import React, { useEffect, useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Loader2, Users, Trophy, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { teamsAPI, mediaUrl } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { ErrorState } from '../components/ErrorState';

export default function Equipe() {
  const { user, updateLocalUser } = useAuth();
  const [mine, setMine] = useState(null);
  const [teams, setTeams] = useState([]);
  const [joining, setJoining] = useState(null);
  const [error, setError] = useState(false);

  const load = () => {
    setError(false);
    teamsAPI.mine().then(setMine).catch(() => setError(true));
    teamsAPI.list().then(setTeams).catch(() => setError(true));
  };
  useEffect(() => { load(); }, []);

  const join = async (team_id) => {
    setJoining(team_id);
    try {
      await teamsAPI.join(team_id);
      if (user) updateLocalUser({ ...user, team_id });
      toast.success('Tu as rejoint l\'équipe !');
      load();
    } catch { toast.error('Échec'); }
    finally { setJoining(null); }
  };

  if (error) return <div className="animate-rise"><ErrorState onRetry={load} /></div>;
  if (!mine) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-red-600" /></div>;

  return (
    <div className="space-y-6 animate-rise">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-red-950 dark:text-red-50">Mon équipe</h1>
        <p className="text-muted-foreground text-sm mt-1">Avancez ensemble, l'entraide fait la force 💪</p>
      </div>

      {mine.team ? (
        <>
          <Card className="p-6 rounded-2xl border-0 text-white relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${mine.team.color}, #0B0B0C)` }} data-testid="my-team-card">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center"><Shield className="w-6 h-6" /></div>
              <div>
                <p className="text-xs opacity-80">Classée #{mine.team.rank}</p>
                <h2 className="text-xl font-extrabold">{mine.team.name}</h2>
              </div>
              <div className="ml-auto text-right">
                <p className="text-2xl font-extrabold">{mine.team.total_points}</p>
                <p className="text-xs opacity-80">points d'équipe</p>
              </div>
            </div>
            <p className="text-sm mt-3 opacity-90">{mine.team.member_count} membre(s) mobilisé(s)</p>
          </Card>

          <Card className="p-5 rounded-2xl border-red-100 dark:border-red-900/50">
            <h3 className="font-bold text-red-950 dark:text-red-50 mb-3 flex items-center gap-2"><Users className="w-5 h-5 text-red-600" />Membres de l'équipe</h3>
            <div className="space-y-2">
              {mine.members?.map((m, i) => (
                <div key={m.user_id} className="flex items-center gap-3 p-2 rounded-xl">
                  <span className="w-5 text-center font-bold text-muted-foreground text-sm">{i + 1}</span>
                  <Avatar className="w-8 h-8"><AvatarImage src={mediaUrl(m.avatar)} /><AvatarFallback className="bg-red-600 text-white text-xs">{m.name?.charAt(0)}</AvatarFallback></Avatar>
                  <span className="flex-1 text-sm font-semibold text-foreground">{m.name}</span>
                  <span className="text-xs text-muted-foreground">🔥{m.current_streak}</span>
                  <span className="text-sm font-extrabold text-orange-500">{m.total_points} pts</span>
                </div>
              ))}
            </div>
          </Card>
        </>
      ) : (
        <Card className="p-5 rounded-2xl border-red-100 dark:border-red-900/50">
          <h3 className="font-bold text-red-950 dark:text-red-50 mb-1">Choisis ton équipe</h3>
          <p className="text-xs text-muted-foreground mb-3">Rejoins une équipe pour partager la motivation.</p>
          <div className="space-y-2">
            {teams.map((t) => (
              <div key={t.team_id} className="flex items-center gap-3 p-2.5 rounded-xl border border-border">
                <span className="w-4 h-4 rounded-full" style={{ background: t.color }} />
                <span className="flex-1 font-semibold text-sm text-foreground">{t.name}</span>
                <span className="text-xs text-muted-foreground">{t.member_count} · {t.total_points} pts</span>
                <Button size="sm" onClick={() => join(t.team_id)} disabled={joining === t.team_id} data-testid={`join-${t.team_id}`} className="bg-orange-500 hover:bg-orange-600 rounded-full text-xs">
                  {joining === t.team_id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Rejoindre'}
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Team leaderboard */}
      <Card className="p-5 rounded-2xl border-red-100 dark:border-red-900/50">
        <h3 className="font-bold text-red-950 dark:text-red-50 mb-3 flex items-center gap-2"><Trophy className="w-5 h-5 text-orange-500" />Classement des équipes</h3>
        <div className="space-y-2" data-testid="team-leaderboard">
          {teams.map((t) => (
            <div key={t.team_id} className={`flex items-center gap-3 p-2.5 rounded-xl ${t.team_id === user?.team_id ? 'bg-red-50 dark:bg-red-900/30' : ''}`}>
              <span className="w-6 text-center font-extrabold text-muted-foreground">{t.rank}</span>
              <span className="w-3.5 h-3.5 rounded-full" style={{ background: t.color }} />
              <span className="flex-1 font-semibold text-sm text-foreground">{t.name}</span>
              <span className="text-xs text-muted-foreground">{t.member_count}👥</span>
              <span className="text-sm font-extrabold text-red-600">{t.total_points} pts</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
